import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import authService from "../services/authService";

const panelToneClasses = {
  success: "border-emerald-400/35 bg-emerald-500/10 text-emerald-100",
  error: "border-rose-400/35 bg-rose-500/10 text-rose-100",
  info: "border-blue-400/30 bg-blue-500/10 text-blue-100",
};

const inputClassName =
  "w-full rounded-xl border border-slate-700/80 bg-slate-950/80 px-4 py-3 text-sm text-slate-50 outline-none transition placeholder:text-slate-500 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20";

const VerifyEmail = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [verificationState, setVerificationState] = useState({
    status: "idle",
    message: "",
  });
  const [email, setEmail] = useState("");
  const [resendState, setResendState] = useState({
    loading: false,
    type: "",
    message: "",
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    if (!token) {
      setVerificationState({
        status: "error",
        message:
          "This verification link is missing its token. Enter your email below and we will send you a fresh verification link.",
      });
      return undefined;
    }

    let isMounted = true;
    let redirectTimer;

    const verifyEmail = async () => {
      setVerificationState({
        status: "loading",
        message: "Verifying your email address...",
      });

      try {
        const response = await authService.verifyEmail(token);

        if (!isMounted) {
          return;
        }

        if (response.status === "success") {
          setEmail(response.data?.email || "");
          setVerificationState({
            status: "success",
            message:
              response.message ||
              "Your email has been verified. Redirecting to login...",
          });
          redirectTimer = window.setTimeout(() => {
            navigate("/login");
          }, 3000);
          return;
        }

        setVerificationState({
          status: "error",
          message:
            response.message ||
            "We could not verify this email link. Please request a new one below.",
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setVerificationState({
          status: "error",
          message:
            error.message ||
            "We could not verify this email link. Please request a new one below.",
        });
      }
    };

    verifyEmail();

    return () => {
      isMounted = false;
      if (redirectTimer) {
        window.clearTimeout(redirectTimer);
      }
    };
  }, [location.search, navigate]);

  const handleResend = async (event) => {
    event.preventDefault();

    if (!email.trim()) {
      setResendState({
        loading: false,
        type: "error",
        message: "Enter the email address tied to your account first.",
      });
      return;
    }

    setResendState({
      loading: true,
      type: "",
      message: "",
    });

    try {
      const response = await authService.resendVerification(email.trim());

      if (response.status === "success") {
        setResendState({
          loading: false,
          type: "success",
          message:
            response.message ||
            "A new verification email has been sent. Please check your inbox.",
        });
        return;
      }

      setResendState({
        loading: false,
        type: "error",
        message:
          response.message ||
          "We could not send another verification email right now.",
      });
    } catch (error) {
      setResendState({
        loading: false,
        type: "error",
        message:
          error.message ||
          "We could not send another verification email right now.",
      });
    }
  };

  const showResendForm = verificationState.status === "error";
  const statusToneClass =
    verificationState.status === "success"
      ? panelToneClasses.success
      : verificationState.status === "error"
        ? panelToneClasses.error
        : panelToneClasses.info;

  return (
    <main className="min-h-screen bg-[#111318] text-slate-200">
      <Header />

      <section className="px-4 py-12 sm:px-6 sm:py-14 lg:px-8 lg:py-16">
        <div className="mx-auto max-w-[560px] rounded-[24px] border border-slate-700/70 bg-[linear-gradient(180deg,rgba(17,24,39,0.96),rgba(15,23,42,0.92))] p-6 shadow-[0_22px_60px_rgba(0,0,0,0.35)] backdrop-blur-[10px] sm:p-8">
          <div className="mb-6 text-center">
            <h2 className="text-3xl font-bold tracking-[-0.02em] text-slate-50">
              Verify Your Email
            </h2>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              We are confirming your account so you can sign in and access the
              research portal.
            </p>
          </div>

          <div
            className={`rounded-2xl border px-4 py-4 text-sm leading-6 ${statusToneClass} ${
              showResendForm ? "mb-6" : "mb-5"
            }`}
          >
            {verificationState.message}
          </div>

          {showResendForm && (
            <form onSubmit={handleResend}>
              <label htmlFor="verification-email" className="mb-2 block text-sm font-semibold text-slate-200">
                Email address
              </label>
              <input
                id="verification-email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className={inputClassName}
                autoComplete="email"
                required
              />

              {resendState.message && (
                <div
                  className={`mt-4 rounded-xl border px-4 py-3 text-sm leading-6 ${
                    resendState.type === "success"
                      ? panelToneClasses.success
                      : panelToneClasses.error
                  }`}
                >
                  {resendState.message}
                </div>
              )}

              <button
                type="submit"
                disabled={resendState.loading}
                className="mt-5 w-full rounded-xl bg-blue-600 px-4 py-3 text-[15px] font-semibold text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:bg-slate-600"
              >
                {resendState.loading
                  ? "Sending verification email..."
                  : "Resend verification email"}
              </button>
            </form>
          )}

          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link
              to="/login"
              className="text-sm font-semibold text-blue-400 transition hover:text-blue-300"
            >
              Go to login
            </Link>
            <Link
              to="/signup"
              className="text-sm font-semibold text-slate-300 transition hover:text-white"
            >
              Create another account
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default VerifyEmail;
